import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { usePermissionsContext } from "../../context/permissionContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { ProcessingRequestStatus, ProcessingRequestStatuses } from "../../http/processingRequest/types";
import useProcessingRequestLabelsPackagingsApi from "../../http/processingRequestLabelsPackagings/api";
import { PackagingAndLabel, RequestPackagingAndLabel } from "../../http/processingRequestLabelsPackagings/types";
import { formatPrice, getHourlyCostFromMinutes } from "../../utils";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";

export interface RequestLabelsAndPackagingsFormBody {
  item: PackagingAndLabel;
  quantity: number;
  unitCost: number;
}

type ProcessingRequestLabelFormProps = {
  item?: RequestPackagingAndLabel;
  requestStatus: ProcessingRequestStatus;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmit: (data: RequestLabelsAndPackagingsFormBody) => void;
};

const RequestLabelsAndPackagingsForm: FC<ProcessingRequestLabelFormProps> = ({
  item,
  requestStatus,
  isDisabled,
  isLoading,
  onSubmit,
}) => {
  const i18n = useI18n();
  const permissions = usePermissionsContext();
  const { searchLabelsAndPackagings } = useProcessingRequestLabelsPackagingsApi();
  const [defaultItemsOptions, setDefaultItemsOptions] = useState<PackagingAndLabel[]>([]);

  const defaultValues = {
    item: item,
    unitCost: item?.unitCost,
    quantity:
      requestStatus === ProcessingRequestStatuses.Open ||
      requestStatus === ProcessingRequestStatuses.Accepted ||
      requestStatus === ProcessingRequestStatuses.WaitingConfirmation ||
      requestStatus === ProcessingRequestStatuses.Refused
        ? item?.expectedQuantity
        : item?.usedQuantity,
  };

  return (
    <Form<RequestLabelsAndPackagingsFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, watch, setValue }) => {
        const quantity = watch("quantity");
        const unitCost = watch("unitCost");

        return (
          <>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.t.common.label}
                  error={fieldState.error}
                  hidden={item?.code === i18n.t.common.minutes.toUpperCase()}
                >
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef<AbortController | undefined>(undefined);
                    const loadOptions = createLoadOptions<PackagingAndLabel>(
                      aborter,
                      searchTerm,
                      searchLabelsAndPackagings,
                    );

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultItemsOptions);
                    }, []);

                    return (
                      <AsyncSelect<PackagingAndLabel>
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultItemsOptions}
                        loadOptions={(st) => loadOptions(st)}
                        getOptionLabel={(o) => o.code}
                        getOptionValue={(o) => o.code}
                        placeholder={i18n.t.common.select + "..."}
                        isDisabled={!!item || isDisabled}
                        styles={reactSelectStyles(isInvalid)}
                        onChange={async (selected) => {
                          !!selected && setValue("item", selected);
                          !!selected?.cost && setValue("unitCost", selected.cost);
                        }}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            {/* Campo costo unitario */}
            <Controller
              name="unitCost"
              control={control}
              rules={{
                required: true,
                min: {
                  value: item?.code === i18n.t.common.minutes.toUpperCase() ? 0.01 : 0.0001,
                  message: i18n.t.common.fieldCannotBeEmpty,
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.costPerUnit} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      min={item?.code === i18n.t.common.minutes.toUpperCase() ? 0.01 : 0.0001}
                      decimalScale={item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      disabled={
                        isDisabled ||
                        isLoading ||
                        requestStatus == ProcessingRequestStatuses.OfferApproved ||
                        requestStatus == ProcessingRequestStatuses.InProgress
                      }
                      suffix=" €"
                      onBlur={(value) => setValue("unitCost", value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="quantity"
              control={control}
              rules={{
                required: true,
                min: {
                  value: 1,
                  message: i18n.t.common.fieldCannotBeEmpty,
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.quantity} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      min={1}
                      decimalScale={0}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      onBlur={(value) => setValue("quantity", value ?? 0)}
                    />
                  )}
                </FormField>
              )}
            />

            {/* Campo costo totale */}
            <FormField label={i18n.t.common.totalCost}>
              <Input
                key={`${unitCost ?? ""}-${quantity ?? ""}`}
                value={
                  unitCost && quantity
                    ? formatPrice(
                        item?.code === i18n.t.common.minutes.toUpperCase()
                          ? getHourlyCostFromMinutes(quantity, unitCost)
                          : unitCost * quantity,
                        permissions.preferredLanguage,
                        {
                          min: 2,
                          max: item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4,
                        },
                      )
                    : formatPrice(0, permissions.preferredLanguage, {
                        min: 2,
                        max: item?.code === i18n.t.common.minutes.toUpperCase() ? 2 : 4,
                      })
                }
                disabled
                readOnly
              />
            </FormField>
          </>
        );
      }}
    </Form>
  );
};

export default RequestLabelsAndPackagingsForm;
